import React, { useState, useEffect, useCallback } from 'react';
import api from '../api/axios';

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────
const fmt = (n) => (n || 0).toLocaleString('en-IN');

const OFFERING_ICONS = {
  'Pushpanjali (Flowers)': '🌸',
  'Deepam (Lamp)':         '🪔',
  'Bhog Arpan (SevaOffering)':   '🍚',
  'Maha Aarti Support':    '🔔',
  'Live Offering':         '🕉️',
};

function getIcon(name) {
  for (const [key, icon] of Object.entries(OFFERING_ICONS)) {
    if (name?.toLowerCase().includes(key.toLowerCase().split(' ')[0].toLowerCase())) return icon;
  }
  return '🙏';
}

// ─────────────────────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────────────────────
const Offerings = () => {
  const [offerings, setOfferings] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [sortBy, setSortBy] = useState('date_desc');

  const fetchOfferings = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/spiritual-activities/for-centre');
      setOfferings(res.data?.data || []);
    } catch (err) {
      console.error('Failed to fetch offerings:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchOfferings(); }, [fetchOfferings]);

  // ── Derived stats ────────────────────────────────────────────────────────
  const totalRevenue  = offerings.reduce((s, o) => s + (o.price || 0), 0);
  const todayCount    = offerings.filter(o =>
    new Date(o.createdAt).toDateString() === new Date().toDateString()
  ).length;

  // Top offering by count
  const countMap = offerings.reduce((acc, o) => {
    const k = o.serviceName || 'General';
    acc[k] = (acc[k] || 0) + 1;
    return acc;
  }, {});
  const topOffering = Object.entries(countMap).sort((a, b) => b[1] - a[1])[0];

  // Unique categories for filter
  const categories = ['all', ...new Set(offerings.map(o => o.category).filter(Boolean))];

  // ── Filtered & sorted list ────────────────────────────────────────────────
  const filtered = offerings
    .filter(o => {
      const term = searchTerm.toLowerCase();
      const matchesSearch =
        o.serviceName?.toLowerCase().includes(term) ||
        o.customerName?.toLowerCase().includes(term) ||
        o.category?.toLowerCase().includes(term);
      const matchesCategory = categoryFilter === 'all' || o.category === categoryFilter;
      return matchesSearch && matchesCategory;
    })
    .sort((a, b) => {
      if (sortBy === 'date_desc')   return new Date(b.createdAt) - new Date(a.createdAt);
      if (sortBy === 'date_asc')    return new Date(a.createdAt) - new Date(b.createdAt);
      if (sortBy === 'price_desc')  return (b.price || 0) - (a.price || 0);
      if (sortBy === 'price_asc')   return (a.price || 0) - (b.price || 0);
      return 0;
    });

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="p-8">

      {/* ── Header ────────────────────────────────────────────────────── */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
        <div>
          <h2 className="font-serif text-3xl font-bold" style={{ color: '#F4EBD8' }}>
            Sacred Offerings
          </h2>
          <p className="mt-1 text-stone-500 text-sm">
            Live-stream ritual bookings — Pushpanjali, Deepam, Bhog Arpan, Maha Aarti &amp; more
          </p>
        </div>

        <button
          onClick={fetchOfferings}
          className="bg-[#FAF9F6] border border-stone-200 text-[#201D42] px-4 py-2 rounded-xl font-bold text-sm hover:bg-stone-100 transition-all flex items-center gap-2"
        >
          <span className="material-symbols-outlined text-[18px]">refresh</span>
          Refresh
        </button>
      </div>

      {/* ── Summary Cards ─────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {[
          {
            label: 'Total Offerings',
            value: offerings.length,
            icon: 'auto_awesome',
            suffix: 'rituals recorded',
            color: '#F4EBD8',
            bg: 'rgba(244,235,216,0.06)',
          },
          {
            label: 'Total Revenue',
            value: `₹${fmt(totalRevenue)}`,
            icon: 'payments',
            suffix: 'sacred earnings',
            color: '#E8B84B',
            bg: 'rgba(232,184,75,0.08)',
          },
          {
            label: "Today's Offerings",
            value: todayCount,
            icon: 'today',
            suffix: 'offered today',
            color: '#34d399',
            bg: 'rgba(5,150,105,0.10)',
          },
          {
            label: 'Most Offered',
            value: topOffering ? getIcon(topOffering[0]) + ' ' + topOffering[0].split(' ')[0] : '—',
            icon: 'workspace_premium',
            suffix: topOffering ? `${topOffering[1]} times` : 'no data yet',
            color: '#a78bfa',
            bg: 'rgba(124,58,237,0.10)',
          },
        ].map(card => (
          <div
            key={card.label}
            className="rounded-2xl p-5"
            style={{ background: card.bg, border: `1px solid ${card.color}20` }}
          >
            <div className="flex items-center justify-between mb-3">
              <span className="text-[10px] font-black uppercase tracking-widest text-stone-500">
                {card.label}
              </span>
              <span
                className="material-symbols-outlined text-[18px]"
                style={{ color: card.color }}
              >
                {card.icon}
              </span>
            </div>
            <div className="text-2xl font-serif font-black" style={{ color: card.color }}>
              {card.value}
            </div>
            <div className="text-[10px] text-stone-400 mt-1">{card.suffix}</div>
          </div>
        ))}
      </div>

      {/* ── Filter Bar ────────────────────────────────────────────────── */}
      <div className="flex flex-wrap gap-3 mb-6">
        {/* Search */}
        <div className="relative flex-1 min-w-[180px]">
          <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 text-sm">
            search
          </span>
          <input
            type="text"
            placeholder="Search devotee or ritual..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-4 py-2 bg-white border border-stone-200 rounded-xl text-sm outline-none focus:border-[#E8B84B] transition-colors"
          />
        </div>

        {/* Category filter */}
        <select
          value={categoryFilter}
          onChange={e => setCategoryFilter(e.target.value)}
          className="px-4 py-2 bg-white border border-stone-200 rounded-xl text-sm outline-none focus:border-[#E8B84B] text-stone-600 font-bold"
        >
          {categories.map(c => (
            <option key={c} value={c}>
              {c === 'all' ? 'All Categories' : c}
            </option>
          ))}
        </select>

        {/* Sort */}
        <select
          value={sortBy}
          onChange={e => setSortBy(e.target.value)}
          className="px-4 py-2 bg-white border border-stone-200 rounded-xl text-sm outline-none focus:border-[#E8B84B] text-stone-600 font-bold"
        >
          <option value="date_desc">Newest First</option>
          <option value="date_asc">Oldest First</option>
          <option value="price_desc">Highest Amount</option>
          <option value="price_asc">Lowest Amount</option>
        </select>
      </div>

      {/* ── Table ─────────────────────────────────────────────────────── */}
      <div
        className="bg-white rounded-2xl overflow-hidden"
        style={{ border: '1px solid rgba(212,175,55,0.2)', boxShadow: '0 8px 30px rgba(0,0,0,0.04)' }}
      >
        {loading ? (
          <div className="py-20 flex justify-center">
            <div className="w-8 h-8 border-4 border-[#201D42]/20 border-t-[#201D42] rounded-full animate-spin" />
          </div>
        ) : (
          <table className="w-full text-left border-collapse">
            <thead>
              <tr
                className="text-xs"
                style={{
                  background: 'rgba(244,235,216,0.05)',
                  borderBottom: '1px solid rgba(212,175,55,0.2)',
                }}
              >
                {['Ritual / Offering', 'Devotee', 'Category', 'Date & Time', 'Amount'].map(h => (
                  <th
                    key={h}
                    className="px-6 py-4 font-bold uppercase tracking-widest"
                    style={{ color: '#F4EBD8' }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-50">
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-16 text-center">
                    <div className="text-5xl mb-4 opacity-20">🕉️</div>
                    <p className="text-stone-400 font-medium">
                      {searchTerm || categoryFilter !== 'all'
                        ? 'No offerings match your filter.'
                        : 'No sacred offerings recorded yet. Offerings from the Live Stream will appear here.'}
                    </p>
                  </td>
                </tr>
              ) : (
                filtered.map((offering, i) => (
                  <tr
                    key={offering._id || i}
                    className="hover:bg-stone-50 transition-colors"
                  >
                    {/* Ritual name */}
                    <td className="px-6 py-5">
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">
                          {getIcon(offering.serviceName)}
                        </span>
                        <div>
                          <div className="font-bold text-stone-800">
                            {offering.serviceName || 'Sacred Offering'}
                          </div>
                          <div className="text-[10px] text-stone-400 font-mono mt-0.5">
                            #{(offering._id || '').slice(-8).toUpperCase()}
                          </div>
                        </div>
                      </div>
                    </td>

                    {/* Devotee */}
                    <td className="px-6 py-5">
                      <div className="font-medium text-stone-700">
                        {offering.customerName || offering.userId?.name || 'Anonymous Devotee'}
                      </div>
                      {offering.userId?.email && (
                        <div className="text-[10px] text-stone-400 mt-0.5">
                          {offering.userId.email}
                        </div>
                      )}
                    </td>

                    {/* Category */}
                    <td className="px-6 py-5">
                      <span
                        className="px-2.5 py-1 rounded-lg text-[10px] font-black uppercase tracking-wide"
                        style={{
                          background: 'rgba(128,0,0,0.06)',
                          color: '#F4EBD8',
                          border: '1px solid rgba(128,0,0,0.12)',
                        }}
                      >
                        {offering.category || 'Live Offering'}
                      </span>
                    </td>

                    {/* Date */}
                    <td className="px-6 py-5">
                      <div className="text-stone-700 text-sm">
                        {new Date(offering.createdAt).toLocaleDateString('en-IN', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric',
                        })}
                      </div>
                      <div className="text-[10px] text-stone-400 mt-0.5">
                        {new Date(offering.createdAt).toLocaleTimeString('en-IN', {
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </div>
                    </td>

                    {/* Amount */}
                    <td className="px-6 py-5">
                      <span
                        className="font-serif font-black text-base"
                        style={{ color: '#E8B84B' }}
                      >
                        ₹{fmt(offering.price)}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>

            {/* Footer total */}
            {filtered.length > 0 && (
              <tfoot>
                <tr style={{ background: '#FAF9F6', borderTop: '1px solid rgba(212,175,55,0.2)' }}>
                  <td colSpan="4" className="px-6 py-4">
                    <span className="text-[10px] font-black uppercase tracking-widest text-stone-400">
                      {filtered.length} of {offerings.length} offerings
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="font-serif font-black text-base" style={{ color: '#E8B84B' }}>
                      ₹{fmt(filtered.reduce((s, o) => s + (o.price || 0), 0))}
                    </span>
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        )}
      </div>
    </div>
  );
};

export default Offerings;
